import { Typography } from '@material-ui/core';
import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { BenefitsList } from '../components/BenefitsList';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { mobileHeroLeadText } from '../components/MarketingTypography';
import { Reveal } from '../components/motion';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const PageHero = styled('div')`
  text-align: center;
  max-width: 860px;
  margin: 140px auto 80px;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin: 100px auto 60px;
  }
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 720px;
  margin: 24px auto 36px;
  ${mobileHeroLeadText}
`;

const ButtonRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const SignupLink = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const StatsWrapper = styled('div')`
  max-width: 900px;
  margin: 0 auto 100px;
  padding: 0 24px;
`;

const StepsSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 100px;
  padding: 0 24px;
`;

const StepsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 48px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled('div')`
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 28px 24px;

  .step {
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 12px;
  }

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0 0 10px;
  }

  p {
    font-size: 15px;
    line-height: 1.6;
    color: #4f4f4f;
    margin: 0;
  }
`;

const BenefitsSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 100px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const BenefitsContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const BenefitsBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const Screenshot = styled('img')`
  width: 100%;
  height: auto;
  border-radius: 12px;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const stats = [
  {
    benefitValue: '200+',
    explanation: 'Premium publishers reachable through one platform'
  },
  {
    benefitValue: '$3B+',
    explanation: 'GMV delivered annually for brands by publishers'
  },
  {
    benefitValue: '5min',
    explanation: 'Time it takes to set up a brand or agency account'
  }
];

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/demand/"
          title="Affilimate Demand: Find and Fund the Right Publishers"
          description="For brands, agencies, and networks: match with publishers already promoting your products through other sellers, and deploy sponsorship campaigns across the network."
        />

        <PageHero>
          <Reveal>
            <Headline component="h1">
              Your budget, deployed where influence already lives
            </Headline>
            <HeroDescription>
              Find mentions of your products linking to other sellers, then fund
              sponsorship campaigns that deploy across their portfolios in days,
              not quarters.
            </HeroDescription>
            <ButtonRow>
              <DemoButton text="Get a demo" variant="contained" />
              <SignupLink
                href="https://demand.affilimate.com/signup"
                target="_blank"
              >
                Create a free account <ArrowRight size={18} />
              </SignupLink>
            </ButtonRow>
          </Reveal>
        </PageHero>

        <StatsWrapper>
          <Reveal>
            <BenefitsList benefits={stats} />
          </Reveal>
        </StatsWrapper>

        <StepsSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Headline component="h2">How demand-side campaigns work</Headline>
            </div>
          </Reveal>
          <StepsGrid>
            <Reveal>
              <StepCard>
                <div className="step">01 · Match</div>
                <h3>Match with publishers</h3>
                <p>
                  Match with publishers promoting your products against millions of
                  pages and links, to discover where they&apos;re currently
                  sending their audience to other sellers.
                </p>
              </StepCard>
            </Reveal>
            <Reveal delay={120}>
              <StepCard>
                <div className="step">02 · Fund</div>
                <h3>Launch targeted offers</h3>
                <p>
                  Offer sponsorships to claim high-intent placements on a CPA,
                  CPC, or flat spend basis. Search and filtering targets the
                  inventory that fits your catalog and goals.
                </p>
              </StepCard>
            </Reveal>
            <Reveal delay={240}>
              <StepCard>
                <div className="step">03 · Scale</div>
                <h3>Measure and expand</h3>
                <p>
                  Real-time visibility into campaign performance, easy budget
                  cut-offs when goals are met, and market intelligence to win
                  and keep ownable links.
                </p>
              </StepCard>
            </Reveal>
          </StepsGrid>
        </StepsSection>

        <BenefitsSection>
          <Reveal>
            <BenefitsContent>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Presence where credibility and AI answers are built
              </Headline>
              <BenefitsBody>
                Premium editorial content doesn't just convert readers. It
                shapes what AI assistants recommend. Campaigns on Affilimate
                put your brand in the content that matters for both.
              </BenefitsBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Measurable GMV growth from proven, high-intent demand
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Capture first-party data by becoming the linked seller
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Maintain visibility in content that shapes AI and LLM results
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Brand presence where credibility and influence are built
                </CheckItem>
              </CheckList>
            </BenefitsContent>
          </Reveal>
          <Reveal delay={150}>
            <Screenshot
              src="/images/amplify-for-merchants.png"
              alt="Amplify for brands and agencies"
            />
          </Reveal>
        </BenefitsSection>

        <FinalCta>
          <Reveal>
            <Headline component="h2">Explore demand solutions today</Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ margin: '16px auto 32px' }}
            >
              Set up a brand or agency account in about five minutes, or talk
              to us about scaling campaigns across the network.
            </Typography>
            <ButtonRow>
              <DemoButton text="Get a demo" variant="contained" />
              <SignupLink
                href="https://demand.affilimate.com/signup"
                target="_blank"
              >
                Create a free account <ArrowRight size={18} />
              </SignupLink>
            </ButtonRow>
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
